import {memo, useState} from 'react';
import {cn} from '@features/ui/utils';
import {useAiThinkingForChat} from '@features/ai/useAiThinking';
import {useClarifyingQuestions} from '@features/ai/streaming/useGeneration';
import {EditableScreenName} from './EditableScreenName';

import {type GenType} from '@features/flow/flowService';
import {getNodeDimensions} from '../utils/nodeDimensions';

interface Props {
  currentChatId: string;
  arrId: number;
  isActiveFrame: boolean;
  isSelected?: boolean;
  genType: GenType;
  isLibraryItem?: boolean;
  hasPartialSelection?: boolean;
  isScreenInfoHovered?: boolean;
}

const ScreenInfoComponent = ({
  currentChatId,
  arrId,
  isActiveFrame,
  isSelected = false,
  genType,
  isLibraryItem = false,
  hasPartialSelection = false,
  isScreenInfoHovered = false,
}: Props) => {
  const baseMaxWidth = getNodeDimensions(genType).width;

  const [isEditable, setIsEditable] = useState(false);

  // Get AI thinking state - zoom logic is now handled by ChatGenerationZoom component
  const isAiThinking = useAiThinkingForChat(currentChatId);
  const hasClarifyingQuestions = useClarifyingQuestions(currentChatId) !== null;
  const thinkingColor =
    isAiThinking && hasClarifyingQuestions
      ? 'var(--orange)'
      : 'var(--ai-thinking)';

  return (
    <div
      data-zoom-chrome='screen-info'
      style={{
        height: 'calc(28px * var(--inverse-zoom, 1))',
        position: 'relative',
      }}
    >
      <div
        onDoubleClick={(e) => {
          e.stopPropagation();
          setIsEditable(true);
        }}
        style={{
          transform: 'scale(var(--inverse-zoom, 1))',
          top: 0,
          transformOrigin: 'left top',
          position: 'absolute',
          zIndex: 41,
        }}
        className={cn(
          'relative flex h-[24px] flex-row items-center gap-[8px] rounded-[24px] text-[12px] font-medium',
          isEditable && 'nodrag nopan',
          'text-[var(--inactive-text)]',
          isAiThinking &&
            !hasClarifyingQuestions &&
            'text-[var(--ai-thinking)]',
          isAiThinking && hasClarifyingQuestions && 'text-[var(--orange)]',
          (((isActiveFrame || isSelected) && !hasPartialSelection) ||
            (hasPartialSelection && isScreenInfoHovered)) &&
            (isLibraryItem
              ? 'text-[var(--component)]'
              : 'text-[var(--primary-hex)]'),
          !hasPartialSelection &&
            (isLibraryItem
              ? 'group-hover:text-[var(--component)]'
              : 'group-hover:text-[var(--primary-hex)]'),
        )}
      >
        <div className='flex flex-row items-center gap-[2px]'>
          <EditableScreenName
            chatId={currentChatId}
            isEditable={isEditable}
            setIsEditable={setIsEditable}
            screenCount={arrId}
            style={{
              maxWidth: `calc(${baseMaxWidth}px * var(--canvas-zoom, 1))`,
              color: isAiThinking ? thinkingColor : undefined,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export const ScreenInfo = memo(ScreenInfoComponent);
